import { Goal, GoalImpactLevel } from '@griply/types';
import { t } from 'i18next';
import React, { useRef } from 'react';
import { ThunderLightningFilled } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { ImpactPickerMenu } from 'shared/components/ui/impact-picker-menu';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from './goal-list-card.style';

const impactIconCountMap: Record<GoalImpactLevel, number> = {
  [GoalImpactLevel.High]: 3,
  [GoalImpactLevel.Medium]: 2,
  [GoalImpactLevel.Low]: 1,
};

export type ImpactCellProps = {
  goal: Goal;
};

export const ImpactCell: React.FC<ImpactCellProps> = ({ goal }) => {
  const { mutate: updateGoal } = useUpdateGoalMutation();
  const impactRef = useRef<HTMLSpanElement>(null);
  const impactButtonRef = useRef<HTMLSpanElement>(null);
  const {
    menuOpen: impactMenuOpen,
    openMenu: openImpactMenu,
    closeMenu: closeImpactMenu,
  } = useOpenMenu();

  const onImpactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openImpactMenu();
  };

  const onImpactChange = (value: GoalImpactLevel | null) => {
    updateGoal({ goal: { id: goal.id, impact: value } });
    closeImpactMenu();
  };

  return (
    <>
      <Styled.DataContainer
        ref={impactRef}
        $type={Styled.DataTypes.Impact}
        $isFrozen={!!goal.frozenAt}
        $isClickable={!goal.frozenAt}
        onClick={!goal.frozenAt ? onImpactClick : undefined}
      >
        {goal.impact ? (
          <Styled.DataButton ref={impactButtonRef}>
            <Styled.ImpactIcons $impact={goal.impact}>
              {Array.from(
                { length: impactIconCountMap[goal.impact] },
                (_, index) => (
                  <Icon key={index} icon={ThunderLightningFilled} />
                ),
              )}
            </Styled.ImpactIcons>
          </Styled.DataButton>
        ) : (
          <Styled.SelectPlaceholder $forceShow={impactMenuOpen}>
            {t('general.labels.select')}
          </Styled.SelectPlaceholder>
        )}
      </Styled.DataContainer>

      {!!goal.impact && (
        <Tooltip
          referenceElement={impactButtonRef}
          label={t(`goal.impact.options.${goal.impact}.tooltip`)}
        />
      )}

      {impactMenuOpen && (
        <ImpactPickerMenu
          referenceElement={impactRef}
          value={goal.impact ?? null}
          onChange={onImpactChange}
          onClose={closeImpactMenu}
        />
      )}
    </>
  );
};
